"use client";

import React from "react";
import Image from "next/image";
import SafeHtml from "@/components/ui/SafeHtml";
import { Button } from "@/components/ui/Button";
import { ArrowIcon } from "@/components/ui/Icons";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import type { IconContentFields } from "@/types/acf";

const lgColsClassMap: Record<number, string> = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
};

export default function IconContent({ data }: { data: IconContentFields }) {
  if (!data || data.hide) return null;

  const rows = Array.isArray(data.icon_content) ? data.icon_content : [];
  const count = Math.min(rows.length, 4);
  if (count === 0) return null;

  const lgColsClass = lgColsClassMap[count] ?? "lg:grid-cols-4";

  return (
    <div id={data.id} className="w-full">
      <div
        className={[
          "grid",
          "grid-cols-1",
          "md:grid-cols-2",
          lgColsClass,
          "gap-y-8",
          "md:gap-x-8",
          "lg:gap-x-0", 
          "items-stretch",
        ].join(" ")}
      >
        {rows.slice(0, 4).map((row, i) => {
          const iconObj = row?.icon && typeof row.icon === "object" ? row.icon : null;
          const iconSrc = iconObj?.url ? toAbsoluteUrl(iconObj.url) : null;

          const title = row?.title ?? "";
          const content = row?.content ?? "";

          const rawHref =
            row?.button && typeof row.button === "object" ? (row.button.url ?? "") : "";
          const isAnchorLink = rawHref.trim().startsWith("#");

          if (!iconSrc && !title && !content && !row?.button) return null;

          return (
            <div
              key={i}
              className={[
                "w-full flex flex-col",
                "md:[&:nth-child(2n)]:border-l",
                "md:[&:nth-child(2n)]:border-fuji-grery-dark", 
                "md:[&:nth-child(2n)]:pl-8",
                "lg:border-l",
                "lg:border-fuji-grery-dark",
                "lg:first:border-none",
                "lg:px-8",
                "lg:first:pl-0",
              ].join(" ")}
            >
              {/* Icon above content */}
              {iconSrc ? (
                <div className="mb-4">
                  <Image
                    src={iconSrc}
                    alt={iconObj?.alt || ""}
                    width={iconObj?.width || 42}
                    height={iconObj?.height || 42}
                    className="h-auto w-auto max-w-[42px]"
                    sizes="42px"
                    priority={false}
                  />
                </div>
              ) : null}

              {title ? (
                <h3 className="text-title-3">
                  <SafeHtml content={title} />
                </h3>
              ) : null}

              {content ? (
                <div
                  className="
                    mt-4 min-w-0 break-words
                    [&_img]:max-w-full [&_img]:h-auto
                    [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto
                  "
                >
                  <SafeHtml content={content} flow />
                </div>
              ) : null}

              {row?.button && typeof row.button === "object" && row.button.url ? (
                <div className="mt-6 w-full flex">
                  <Button
                    as="link"
                    href={toAbsoluteUrl(row.button.url) ?? "#"}
                    target={row.button.target || "_self"}
                    rel={row.button.target === "_blank" ? "noopener noreferrer" : undefined}
                    icon={isAnchorLink ? <ArrowIcon direction="down" /> : undefined}
                  >
                    {row.button.title || "Learn more"}
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
